/**
 * Formatters purs pour l'écran Mon utilisation IA (BL-0246).
 */

export type U2106QuotaBarTone = 'ok' | 'warning' | 'blocked' | 'unlimited';

/** Clamp 0–100 pour barre visuelle ; null → 0 (pas de barre trompeuse si illimité). */
export function u2106QuotaBarWidthPercent(usagePercent: number | null): number {
  if (usagePercent == null || Number.isNaN(usagePercent)) {
    return 0;
  }
  return Math.min(100, Math.max(0, usagePercent));
}

/**
 * Tone jauge : illimité si quota null ; sinon blocked ≥100 / warning ≥80 / ok.
 * `blocked` du fournisseur force l'axe concerné en erreur via le caller.
 */
export function u2106ResolveQuotaBarTone(
  usagePercent: number | null,
  quotaMonthlyTokens: number | null,
  forceBlocked = false,
): U2106QuotaBarTone {
  if (quotaMonthlyTokens == null) {
    return 'unlimited';
  }
  if (forceBlocked || (usagePercent != null && usagePercent >= 100)) {
    return 'blocked';
  }
  if (usagePercent != null && usagePercent >= 80) {
    return 'warning';
  }
  return 'ok';
}

export function u2106QuotaBarFillClass(tone: U2106QuotaBarTone): string {
  switch (tone) {
    case 'blocked':
      return 'bg-red-500';
    case 'warning':
      return 'bg-amber-500';
    case 'ok':
      return 'bg-primary-600';
    default:
      return 'bg-gray-300';
  }
}

export function u2106QuotaPercentTextClass(tone: U2106QuotaBarTone): string {
  switch (tone) {
    case 'blocked':
      return 'font-medium text-red-600';
    case 'warning':
      return 'font-medium text-amber-600';
    default:
      return 'text-gray-500';
  }
}

/** Affiche un nombre de jetons selon la locale. */
export function u2106FormatTokenCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** Affiche un pourcentage d'usage (1 décimale max) ou null. */
export function u2106FormatUsagePercent(usagePercent: number | null, locale: string): string | null {
  if (usagePercent == null || Number.isNaN(usagePercent)) {
    return null;
  }
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(usagePercent);
}

/**
 * Libellé du mois civil depuis `period.start` (yyyy-MM-dd), ex. « juillet 2026 ».
 * Arithmétique UTC volontaire : jour civil métier, pas fuseau navigateur.
 */
export function u2106FormatAiUsagePeriodMonth(startYmd: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startYmd.trim());
  if (!match) {
    return startYmd;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = new Date(Date.UTC(year, month - 1, 1));
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(day);
}
